import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { DEVICE_SIZE, mq } from '@marty-js/design/src/utils/mq';
import { useTranslation } from '@marty-js/design/src/utils/translation';
import { HeadingOne } from '@marty-js/design/src/atoms/h1';
import type { TopicInfo, GenericItem } from '@marty-js/api-sdk/types';
import { HeadingTwo } from '@marty-js/design/src/atoms/h2';
import type { SponsoredType } from '@marty-js/design/src/atoms/types';
import { Slider } from '@marty-js/design/src/molecules/slider';
import { SimpleItemProductCard } from '@marty-js/design/src/molecules/cards/product/simpleItem';
import { ToolkitItem } from '@marty-js/design/src/molecules/toolkit-item';
import { useTopicQuery } from '@marty-js/api-sdk/services/sso';
import { GoogleNewsSubscribe } from '@marty-js/design/src/atoms/google-news-subscribe';
import { BottomNavCustom, handleShare } from '@marty-js/design/src/molecules/bottom-navigation-custom';
import { StickyComponent } from '@marty-js/design/src/atoms/sticky-footer';
import { Insert } from '@marty-js/design/src/molecules/article/insert';
import type { InsertProps } from '@marty-js/design/src/molecules/article/insert';
import { Inform } from '@marty-js/design/src/atoms/inform/inform';
import { useTrackEvent } from '@marty-js/api-sdk/services/matomo';
import dynamic from 'next/dynamic';
import { Ad } from '../../../ads/ad';
import { Breadcrumb } from '../../../atoms/breadcrumb';
import { DateDisplay } from '../../../atoms/date/date';
import type { LayoutItemProps, StickyFooter } from '../types';
import { Background, Container, FullContentRelative, MainContent, SideContent } from '../../../atoms/grid-container';
import { EditoGrid } from '../../../article-processor/edito-grid';
import type { Link } from '../../../article-processor/types';
import { useSetMaxFirstImages } from '../../../utils/imageContext';
import { Comments } from '../../../atoms/Comments';
import type { ItemSidebarComponents } from '../../../layout/sidebar/types';
import ItemSidebar from '../../../layout/sidebar/itemSidebar';
import { AuthorLine } from '../../../atoms/author-line';
import { AuthorDetailed } from '../../../atoms/author-detailed';
import { CommentsStatic } from '../../../atoms/CommentsStatic';
import { DedicatedItemMenu } from '../../../layout/menu/dedicated-item-menu';

const NewsIcon = dynamic(() => import('@marty-js/design/src/icons/material-symbols/news'));
const CommentIcon = dynamic(() => import('@marty-js/design/src/icons/comments'));
const ShareIcon = dynamic(() => import('@marty-js/design/src/icons/material-symbols/share'));

const LeftSide = styled.div`
  position: relative;
`;

const MainTitleItem = styled(HeadingOne)`
  color: var(--theme-color-title);
  grid-column: 2 / 3;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      grid-column: 1 / 10;

      &.mod-margin {
        margin: 0 0 30px;
      }
    `,
  )}
`;

export const SideContentSticky = styled.div`
  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      position: sticky;
      top: 20px;

      &.mod-lower {
        top: 80px;
      }

      &.mod-spacer {
        margin: 30px 0;
      }
    `,
  )};
`;

export type GuideData = {
  mainArticle: {
    articleJson: any;
    index?: Link[];
    firstTitleRow?: string;
  };
  insertArticle: {
    data: InsertProps;
  };
  tutorials: {
    itemList: GenericItem[];
  };
  lastGuides: {
    genericItemList: GenericItem[];
    linkAll?: {
      href: string;
      label: string;
    };
  };
  sticky: {
    dataComponent: {
      data: {
        data: StickyFooter;
      };
    };
  };
  comments: {
    commentsRaw: TopicInfo;
  };
  dedicatedItemLinks: {
    dataComponent: {
      items: {
        genericItemList: GenericItem[];
      };
    };
  };
} & ItemSidebarComponents;

const Guide: React.FC<LayoutItemProps<GuideData>> = ({ item, data, breadcrumb }) => {
  const t = useTranslation();

  useSetMaxFirstImages(3);
  const refSideBar = useRef<HTMLElement>();

  const { topic, loading, fetchData, fetchMoreComments } = useTopicQuery();
  const [initialLoad, setInitialLoad] = useState(true);
  const [offset, setOffset] = useState(0);
  const [trackEvent] = useTrackEvent();

  useEffect(() => {
    if (initialLoad) {
      fetchData({ variables: { id: item.id, userToken: localStorage.getItem('userToken') } });
    }
    setInitialLoad(false);
  }, [fetchData, initialLoad, item.id]);

  const loadMoreComments = () => {
    const newOffset = offset + 19;
    fetchMoreComments({
      id: item.id,
      userToken: localStorage.getItem('userToken'),
      offset: newOffset,
    });
    setOffset(newOffset);
  };

  const itemTutorials = useMemo(() => {
    const turorials = data?.lastGuides?.genericItemList;

    if (!turorials) {
      return null;
    }

    return (
      turorials.map((genericItem: GenericItem & { reviewUrl?: string }) => {
        const itemLink = {
          href: genericItem.url,
        };

        return (
          <SimpleItemProductCard
            key={genericItem.id}
            image={{ imageId: genericItem.imageId, alt: genericItem.title }}
            title={genericItem.title}
            itemLink={itemLink}
            nbComments={genericItem?.commentsNb}
            {...genericItem}
            isSmall
          />
        );
      }) ?? null
    );
  }, [data]);

  const linkToGuides = useMemo(() => {
    if (data?.lastGuides?.linkAll) {
      return {
        href: data.lastGuides.linkAll.href,
        label: t('sdk.item.all_guides', { sectionTitle: data.lastGuides.linkAll.label }),
      };
    }

    return null;
  }, [data, t]);

  const hasAuthor = item.linkedWith?.author != null;

  const inserts: ReactNode[] = useMemo(() => {
    if (data.insertArticle?.data && !item.tagSlugs.includes('sponsored')) {
      return [<Insert {...data.insertArticle.data} key="insert1" />];
    }

    return null;
  }, [data.insertArticle.data, item.tagSlugs]);

  const sponsored: SponsoredType = {
    isSponsored: item.tagSlugs.includes('sponsored'),
    sponsors: item.sponsors || [],
  };

  return (
    <>
      <Ad className="Billboard_1" desktopOnly megaban />
      <Container>
        <MainContent>
          <Breadcrumb breadcrumb={breadcrumb} />
          <MainTitleItem className={hasAuthor ? 'mod-margin' : ''}>{item.title}</MainTitleItem>
          <DedicatedItemMenu itemList={data?.dedicatedItemLinks?.dataComponent?.items?.genericItemList} />
          <LeftSide>
            <AuthorLine authors={item.linkedWith?.author} nbComments={false} sponsored={sponsored}>
              {item.updatedAt > item.publishedAt ? (
                <DateDisplay dateTime={item.updatedAt} pattern={t('localeTimePattern')} />
              ) : (
                <DateDisplay dateTime={item.publishedAt} pattern={t('localeTimePattern')} />
              )}
            </AuthorLine>
          </LeftSide>
          <EditoGrid
            articleJson={data.mainArticle.articleJson}
            links={data.mainArticle.index}
            firstTitleRow={data.mainArticle.firstTitleRow}
            insertHeadings={inserts}
          />
          <AuthorDetailed authors={item.linkedWith?.author} />
          <GoogleNewsSubscribe />
          {data.sticky?.dataComponent?.data?.data?.name && (
            <StickyComponent
              name={data.sticky.dataComponent.data.data.name}
              price={data.sticky.dataComponent.data.data.price}
              button={data.sticky.dataComponent.data.data.button}
              landingPage={data.sticky.dataComponent.data.data.landingPage}
              svg={data.sticky.dataComponent.data.data.svg}
              trackEvent={trackEvent}
            />
          )}
          <Inform sponsored={sponsored} tags={item?.tagSlugs} />
          {!topic?.closed && (
            <Comments
              loadMoreComments={loadMoreComments}
              offset={0}
              topic={topic}
              fetchData={fetchData}
              itemId={item.id}
              loading={loading}
            />
          )}
          {data?.comments?.commentsRaw && data?.comments?.commentsRaw?.postStream?.posts?.length > 0 && (
            <CommentsStatic topic={data.comments.commentsRaw} />
          )}
        </MainContent>
        <SideContent ref={refSideBar}>
          <ItemSidebar refContainer={refSideBar} components={data} />
        </SideContent>
        {itemTutorials && (
          <FullContentRelative className="mod-spacer-top">
            <HeadingTwo className="mod-fat" as="div">
              {t('sdk.item.last_guides')}
            </HeadingTwo>
            <Slider items={itemTutorials} bottomLink={linkToGuides || null} />
            <Background />
          </FullContentRelative>
        )}
      </Container>
      <ToolkitItem title={item.title} />
      {!topic?.closed && (
        <BottomNavCustom
          navigations={[
            {
              icon: <NewsIcon width={22} height={22} color="var(--title-foreground)" />,
              label: t('design.bottomNav.news'),
              type: 'href',
              href: '/actualites-informatique/',
              onClick: () => {},
            },
            {
              icon: <ShareIcon width={22} height={22} color="var(--title-foreground)" />,
              label: t('design.bottomNav.share'),
              type: 'share',
              href: null,
              onClick: () => {
                handleShare(item.title, item.url, item.lead);
              },
            },
            {
              icon: <CommentIcon width={22} height={22} color="var(--title-foreground)" />,
              label: t('design.bottomNav.comments'),
              type: 'comments',
              href: '#comments',
              exponentLabel: topic?.postsCount.toString() ?? '0',
              onClick: () => {},
            },
          ]}
        />
      )}
    </>
  );
};

export default Guide;
